import { createApiRef } from '@backstage/core-plugin-api';
import {
  CapabilitiesDto,
  DeleteInfraLaunchDto,
  InfraWorkflowDetailDto,
  InfraWorkflowDto,
  PollerStatusDto,
} from './types';

/**
 * Read access to the infrastructure workflows tracked by the
 * `autom2plataforma` backend.
 */
export interface Autom2PlataformaApi {
  /**
   * Infrastructures linked to a catalog entity, most recent first.
   *
   * The backend authorises this listing against the Application's
   * `can_view_details` relation, so a denied caller gets an error rather than an
   * empty list — the two must not be conflated in the UI.
   */
  listWorkflowsByEntity(entityRef: string): Promise<InfraWorkflowDto[]>;

  /**
   * A single infrastructure with its outputs bag.
   *
   * Addressed by `workflowId` (the AUTOM2PLATAFORMA `launchwf` response
   * `id`), not `contextId`: a `context_id` is now stable and reused across
   * every environment of the same Servicio de Aplicación, so it no longer
   * identifies a single row.
   *
   * Rejects with a 403 `Autom2PlataformaRequestError` when the caller lacks
   * `can_view_details` on the owning Application, and also when the stored row
   * has no `entityRef` to authorise against — the two are indistinguishable
   * from here by design.
   */
  getWorkflow(workflowId: string): Promise<InfraWorkflowDetailDto>;

  /**
   * Resolves the real value(s) of a redacted `secrets` node, on demand, from
   * the prod account's Secrets Manager. Only works for `kind: 'redacted'`
   * leaves whose `redaction.reason` is `'contract'` — the backend rejects
   * anything else with 403. The caller must never persist the result: it is
   * resolved fresh on every call and is not meant to outlive this request.
   */
  revealSecret(workflowId: string, path: string): Promise<Record<string, string>>;

  /** Health of the backend's scheduled poller. */
  getPollerStatus(): Promise<PollerStatusDto>;

  /**
   * What the caller may do. `canDeleteInfra` mirrors the server-side check of
   * `POST …/delete-infra`; it only decides what the UI offers — the backend
   * enforces it again on the action itself.
   */
  getCapabilities(): Promise<CapabilitiesDto>;

  /**
   * Launches the AUTOM2PLATAFORMA "delete infra" workflow for one
   * infrastructure and marks it destroyed in the same request: there is no
   * intermediate state to wait for, so a resolved call means the infrastructure
   * is already marked `DESTROYED`. Rejects with an
   * `Autom2PlataformaRequestError` carrying the HTTP status: 403 (not a
   * platform admin), 404 (unknown or already destroyed), 409 (not deletable in
   * its current status — it is still `RUNNING` — or a concurrent request
   * destroyed it first), 502 (AUTOM2PLATAFORMA rejected the launch; the backend
   * attempts to revert the destroyed mark so the infrastructure is left as it
   * was, but if that revert itself fails the infrastructure stays marked
   * `DESTROYED` — the status code alone cannot tell the two cases apart).
   */
  launchDeleteInfra(workflowId: string): Promise<DeleteInfraLaunchDto>;
}

export const autom2plataformaApiRef = createApiRef<Autom2PlataformaApi>({
  id: 'plugin.autom2plataforma.service',
});
